import * as React from 'react'

import Seo from '../components/seo'
import Layout from '../components/layout'
import Page from '../components/page'
import { Canvas } from '@react-three/fiber'
import WaveMatrix from '../components/wave-matrix'
import innovative from '../images/innovative.svg'
import productivity from '../images/productivity.svg'
import entertainment from '../images/entertainment.svg'
import facebook from '../images/facebook.svg'
import twitter from '../images/twitter.svg'
import youtube from '../images/youtube.svg'
import { graphql } from 'gatsby'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import { htmlOptions } from '../contenful'
import ImageSlider from '../components/image-slider'

const offers = [
  {
    title: 'Innovative Solutions',
    image: innovative,
  },
  {
    title: 'Productivity Tools',
    image: productivity,
  },
  {
    title: 'Entertainment Options',
    image: entertainment,
  },
]

const IndexPage = ({
  data: {
    allContentfulShowCases: { nodes },
  },
}) => {
  const [showCaseIndex, setShowCaseIndex] = React.useState(0)
  const showCase = React.useMemo(
    () => nodes[showCaseIndex],
    [nodes, showCaseIndex],
  )
  const showCaseMobileHeight = React.useMemo(() => {
    let maxHeight = 0
    let ratio = 1
    for (let i = 0; i < showCase.images.length; i++) {
      const image = showCase.images[i]
      if (image.height > maxHeight) {
        maxHeight = image.height
        ratio = image.width / image.height
      }
    }

    return typeof window !== 'undefined' ? window.innerWidth / ratio : ratio
  }, [showCase])

  const nextShowCase = React.useCallback(() => {
    setShowCaseIndex(i => (i + 1) % nodes.length)
  }, [nodes.length])

  const previousShowCase = React.useCallback(() => {
    setShowCaseIndex(i => (i - 1 + nodes.length) % nodes.length)
  }, [nodes.length])

  return (
    <Layout title="Tenolife" light={false}>
      <Page className="flex flex-col items-center">
        <div className="h-[10vh]" />
        <h1 className="text-white text-2xl lg:text-4xl font-medium">
          Tech for your best life
        </h1>
        <button className="bg-white text-black px-4 py-2 mt-8 rounded-md font-serif">
          Learn more
        </button>
      </Page>
      <Canvas
        style={{
          position: 'absolute',
          top: 64,
          height: '100%',
          minHeight: 640,
        }}
      >
        <WaveMatrix />
      </Canvas>
      <Page className="flex flex-col items-center h-auto lg:h-screen">
        <h1 className="text-white text-2xl lg:text-4xl font-medium font-serif">
          What We Offer
        </h1>
        <div className="flex flex-col items-center lg:flex-row justify-between mt-20">
          {offers.map((offer, index) => (
            <div key={index} className="flex flex-col items-center w-1/3 mx-4">
              <img src={offer.image} alt={offer.title} />
              <h2 className="text-white text-lg lg:text-xl font-semibold mt-4">
                {offer.title}
              </h2>
            </div>
          ))}
        </div>
      </Page>
      <Page className="flex flex-col items-center h-auto mt-20 lg:h-screen lg:mt-0">
        <h1 className="text-white text-2xl lg:text-4xl font-medium">Gallery</h1>
        <div className="grow flex flex-col items-center lg:flex-row lg:items-stretch my-20 w-full">
          <div
            className="inline mb-8 lg:hidden w-full border-8 rounded-md border-black-5"
            style={{
              backgroundColor: showCase.bgColor,
              height: showCaseMobileHeight,
            }}
          >
            <ImageSlider images={showCase.images} />
          </div>
          <div className="lg:w-1/2 lg:pr-8 flex flex-col">
            <h2 className="text-white text-xl lg:text-3xl font-semibold">
              {showCase.title}
            </h2>
            <p className="my-8 lg:text-lg font-serif">
              {renderRichText(
                showCase.description,
                htmlOptions(showCase.description),
              )}
            </p>
            <div className="grow w-full flex flex-row justify-center items-start">
              <a
                className="bg-black text-white px-4 py-2 rounded-md"
                href={showCase.link}
              >
                Learn more
              </a>
            </div>
            <div className="w-full flex flex-row justify-between items-center mt-4">
              <button
                className="w-0 h-0 border-t-[16px] border-t-transparent border-r-[32px] border-r-black-5 border-b-[16px] border-b-transparent"
                onClick={previousShowCase}
              ></button>
              <div className="text-black-5 font-semibold text-2xl">
                {showCaseIndex + 1} of {nodes.length}
              </div>
              <button
                className="w-0 h-0 border-t-[16px] border-t-transparent border-l-[32px] border-l-black-5 border-b-[16px] border-b-transparent"
                onClick={nextShowCase}
              ></button>
            </div>
          </div>
          <div
            className="hidden lg:block w-1/2 border-8 rounded-md border-black-5"
            style={{ backgroundColor: showCase.bgColor }}
          >
            <ImageSlider images={showCase.images} />
          </div>
        </div>
      </Page>
      <Page className="flex flex-col items-center justify-center">
        <h1 className="text-black text-2xl lg:text-4xl font-medium font-serif">
          Talk to Us
        </h1>
        <div className="w-full bg-black-5 mt-20 rounded-md p-8 flex flex-col">
          <div className="flex flex-row w-full">
            <input />
          </div>
          <div className="flex flex-row w-full justify-end">
            <button className="bg-white text-black px-4 py-2 rounded-md font-serif">
              Submit
            </button>
          </div>
        </div>
        <div className="mt-20 text-black text-xl">support@tenolife.com</div>
        <div className="mt-8 flex flex-row gap-x-4">
          <img src={facebook} alt="Facebook" />
          <img src={twitter} alt="Twitter" />
          <img src={youtube} alt="Youtube" />
        </div>
      </Page>
    </Layout>
  )
}

export const query = graphql`
  query {
    allContentfulShowCases(
      filter: { enabled: { eq: true } }
      sort: { order: ASC }
    ) {
      nodes {
        id
        title
        description {
          raw
        }
        link
        bgColor
        images {
          title
          url
          width
          height
        }
      }
    }
  }
`

/**
 * Head export to define metadata for the page
 *
 * See: https://www.gatsbyjs.com/docs/reference/built-in-components/gatsby-head/
 */
export const Head = () => (
  <Seo title="Home">
    <body className="bg-gradient-to-b from-black to-white" />
  </Seo>
)

export default IndexPage
